import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Form, Input, Button, DatePicker, Upload, Space, Typography, Row, Col,
  message, Spin, Tag, Modal
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, ClockCircleOutlined,
  PictureOutlined, AudioOutlined, VideoCameraOutlined,
  DeleteOutlined, UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { articlesAPI } from '../../utils/api';
import { uploadImage, uploadAudio, uploadVideo, generateVideoThumbnail } from '../../utils/caminoUploads';
import CaminoRichEditor from '../../components/CaminoRichEditor';
import './CaminoEditor.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SECTION_ETAPAS = 'Camino — Etapas';

// Status derivado: si fecha es futura → scheduled; si no → published. Draft se decide aparte.
const computeStatus = (dateObj, isDraft) => {
  if (isDraft) return 'draft';
  if (dateObj && dayjs(dateObj).isAfter(dayjs())) return 'scheduled';
  return 'published';
};

const EtapaEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [mediaPublishDate, setMediaPublishDate] = useState(null);

  const loadEtapa = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await articlesAPI.getArticleById(id);
      if (!res.success) throw new Error(res.error || 'No se pudo cargar la entrada');
      const a = res.data;
      if (a.section !== SECTION_ETAPAS) {
        message.warning('Esta entrada no es una Etapa del Camino.');
        navigate('/camino');
        return;
      }
      form.setFieldsValue({
        title: a.title || '',
        subtitle: a.subtitle || '',
        content: a.content || '',
        date: a.date ? dayjs(a.date) : dayjs()
      });
      setImageUrl(a.imageUrl || null);
      setAudioUrl(a.audioUrl || null);
      setVideoUrl(a.videoUrl || null);
      setVideoThumbnail(a.videoThumbnail || null);
      setMediaPublishDate(a.mediaPublishDate ? dayjs(a.mediaPublishDate) : null);
    } catch (error) {
      console.error('Error cargando etapa:', error);
      message.error('Error al cargar la etapa');
      navigate('/camino');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, form, navigate]);

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ date: dayjs() });
    } else {
      loadEtapa();
    }
  }, [isNew, loadEtapa, form]);

  const beforeImage = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('Solo se permiten imágenes');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error('La imagen excede 10 MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleImageUpload = async ({ file }) => {
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      setImageUrl(url);
      message.success('Imagen subida');
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error || 'Error subiendo imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const beforeAudio = (file) => {
    if (!file.type.startsWith('audio/')) {
      message.error('Solo se permiten audios');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleAudioUpload = async ({ file }) => {
    setUploadingAudio(true);
    try {
      const url = await uploadAudio(file);
      setAudioUrl(url);
      message.success('Audio subido');
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error || 'Error subiendo audio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const beforeVideo = (file) => {
    if (!file.type.startsWith('video/')) {
      message.error('Solo se permiten videos');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleVideoUpload = async ({ file }) => {
    setUploadingVideo(true);
    try {
      const url = await uploadVideo(file);
      setVideoUrl(url);
      message.success('Video subido');

      // Generar thumbnail automáticamente (best effort)
      try {
        message.loading({ content: 'Generando thumbnail…', key: 'thumb', duration: 0 });
        const thumbFile = await generateVideoThumbnail(file);
        const thumbUrl = await uploadImage(thumbFile);
        setVideoThumbnail(thumbUrl);
        message.success({ content: 'Thumbnail generado', key: 'thumb' });
      } catch (thumbErr) {
        console.warn('No se pudo generar thumbnail:', thumbErr);
        message.info({ content: 'Video subido (sin thumbnail automático)', key: 'thumb' });
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error || 'Error subiendo video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (isDraft) => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const dateIso = values.date ? values.date.toISOString() : new Date().toISOString();
      const payload = {
        title: values.title.trim(),
        subtitle: (values.subtitle || '').trim() || null,
        content: values.content || '',
        section: SECTION_ETAPAS,
        date: dateIso,
        status: computeStatus(values.date, isDraft),
        imageUrl: imageUrl || null,
        hasAudio: !!audioUrl,
        audioUrl: audioUrl || null,
        hasVideo: !!videoUrl,
        videoUrl: videoUrl || null,
        videoType: videoUrl ? 'mp4' : null,
        videoThumbnail: videoThumbnail || null,
        mediaPublishDate: mediaPublishDate ? mediaPublishDate.toISOString() : null
      };

      const res = isNew
        ? await articlesAPI.createArticle(payload)
        : await articlesAPI.updateArticle(id, payload);

      if (!res.success) throw new Error(res.error || 'Error al guardar');

      const where = payload.status === 'scheduled'
        ? `programada para ${dayjs(dateIso).format('DD/MM/YYYY HH:mm')}`
        : payload.status === 'draft' ? 'guardada como borrador' : 'publicada';
      message.success(`Etapa ${where}`);
      navigate('/camino');
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error guardando etapa:', error);
      message.error(error?.response?.data?.error || 'Error al guardar la etapa');
    } finally {
      setSaving(false);
    }
  };

  const previewStatus = Form.useWatch('date', form);
  const statusTag = useMemo(() => {
    if (!previewStatus) return null;
    if (dayjs(previewStatus).isAfter(dayjs())) {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>Se programará</Tag>;
    }
    return <Tag color="green">Se publicará inmediatamente</Tag>;
  }, [previewStatus]);

  if (loading) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  return (
    <div className="camino-editor" style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/camino')}>
              Volver
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {isNew ? 'Nueva Etapa' : 'Editar Etapa'}
            </Title>
          </Space>
          <div>
            <Text type="secondary">Sección: {SECTION_ETAPAS}</Text>
          </div>
        </Col>
        <Col>{statusTag}</Col>
      </Row>

      <Form form={form} layout="vertical">
        <Card title="Datos de la etapa" style={{ marginBottom: 16 }}>
          <Form.Item
            name="title"
            label="Título"
            rules={[
              { required: true, message: 'El título es obligatorio' },
              { min: 3, message: 'Mínimo 3 caracteres' }
            ]}
          >
            <Input placeholder="Ej.: Etapa 3 — Sarria a Portomarín" maxLength={200} showCount />
          </Form.Item>

          <Form.Item name="subtitle" label="Subtítulo (opcional)">
            <TextArea rows={2} placeholder="Resumen breve, kilómetros, dificultad…" maxLength={300} showCount />
          </Form.Item>

          <Form.Item
            name="date"
            label="Fecha de publicación"
            rules={[{ required: true, message: 'Indica la fecha' }]}
            extra="Si es futura, la etapa quedará programada y aparecerá en la app en ese momento."
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              placeholder="Selecciona fecha y hora"
            />
          </Form.Item>
        </Card>

        <Card title="Multimedia" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
            <div style={{ marginBottom: 6 }}>
              <Text strong><ClockCircleOutlined /> Aparición de audio/video</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Opcional. Si se rellena, el audio y el video se mantienen ocultos en la app hasta esa fecha, aunque la etapa ya esté publicada. Déjalo vacío para que se vean junto con el resto del contenido.
            </Text>
            <Space wrap>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                value={mediaPublishDate}
                onChange={(d) => setMediaPublishDate(d)}
                placeholder="Sin programar — visible desde publicación"
                style={{ width: 320 }}
              />
              {mediaPublishDate && (
                <Button size="small" onClick={() => setMediaPublishDate(null)}>Quitar programación</Button>
              )}
              {mediaPublishDate && dayjs(mediaPublishDate).isAfter(dayjs()) && (
                <Tag color="orange" icon={<ClockCircleOutlined />}>
                  Audio/video visibles el {dayjs(mediaPublishDate).format('DD/MM HH:mm')}
                </Tag>
              )}
            </Space>
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text strong><PictureOutlined /> Imagen destacada</Text>
              </div>
              {imageUrl ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <img src={imageUrl} alt="destacada"
                    style={{ width: '100%', borderRadius: 8, maxHeight: 240, objectFit: 'cover' }} />
                  <Button danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() => setImageUrl(null)}>Quitar</Button>
                </div>
              ) : null}
              <Upload
                customRequest={handleImageUpload}
                beforeUpload={beforeImage}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} loading={uploadingImage}>
                  {imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
                </Button>
              </Upload>
            </Col>

            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text strong><AudioOutlined /> Audio matutino</Text>
              </div>
              {audioUrl ? (
                <div style={{ marginBottom: 8 }}>
                  <audio src={audioUrl} controls style={{ width: '100%' }} />
                  <Button danger size="small" icon={<DeleteOutlined />}
                    style={{ marginTop: 4 }} onClick={() => setAudioUrl(null)}>
                    Quitar audio
                  </Button>
                </div>
              ) : null}
              <Upload
                customRequest={handleAudioUpload}
                beforeUpload={beforeAudio}
                showUploadList={false}
                accept="audio/*"
              >
                <Button icon={<UploadOutlined />} loading={uploadingAudio}>
                  {audioUrl ? 'Cambiar audio' : 'Subir audio'}
                </Button>
              </Upload>
            </Col>

            <Col xs={24}>
              <div style={{ marginBottom: 8 }}>
                <Text strong><VideoCameraOutlined /> Video (opcional)</Text>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  Se genera thumbnail automático a partir del primer frame útil.
                </Text>
              </div>
              {videoUrl ? (
                <div style={{ marginBottom: 8 }}>
                  <video src={videoUrl} controls style={{ width: '100%', maxHeight: 340, background: '#000' }} />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {videoThumbnail && (
                      <img src={videoThumbnail} alt="thumbnail"
                        style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                    )}
                    <Button danger size="small" icon={<DeleteOutlined />}
                      onClick={() => { setVideoUrl(null); setVideoThumbnail(null); }}>
                      Quitar video
                    </Button>
                  </div>
                </div>
              ) : null}
              <Upload
                customRequest={handleVideoUpload}
                beforeUpload={beforeVideo}
                showUploadList={false}
                accept="video/*"
              >
                <Button icon={<UploadOutlined />} loading={uploadingVideo}>
                  {videoUrl ? 'Cambiar video' : 'Subir video'}
                </Button>
              </Upload>
            </Col>
          </Row>
        </Card>

        <Card title="Contenido" style={{ marginBottom: 16 }}>
          <Form.Item name="content" valuePropName="value">
            <CaminoRichEditor placeholder="Datos de la ruta, albergue, recomendaciones…" />
          </Form.Item>
        </Card>

        <Card>
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={() => handleSubmit(true)}
              loading={saving}
            >
              Guardar borrador
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => handleSubmit(false)}
              loading={saving}
            >
              {previewStatus && dayjs(previewStatus).isAfter(dayjs())
                ? 'Programar'
                : 'Publicar ahora'}
            </Button>
            <Button onClick={() => {
              Modal.confirm({
                title: '¿Descartar cambios?',
                content: 'Volverás al listado sin guardar.',
                okText: 'Descartar', cancelText: 'Seguir editando',
                okButtonProps: { danger: true },
                onOk: () => navigate('/camino')
              });
            }}>Cancelar</Button>
          </Space>
        </Card>
      </Form>
    </div>
  );
};

export default EtapaEditor;
